import React from 'react';
import { Link } from 'react-router-dom';
import { Target, ArrowLeft, AlertTriangle, Share2, Copyright, Cookie } from 'lucide-react';
import SEOHead from '@/components/SEOHead';

const LegalPage = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title="Disclaimer, Sharing Guidelines & Legal — O4S.ai" path="/legal" />

      <nav className="glass-nav sticky top-0 z-50 px-4 md:px-8 py-3 border-b border-earth-200">
        <div className="max-w-4xl mx-auto flex items-center gap-3">
          <Link to="/" className="flex items-center gap-2 text-earth-500 hover:text-earth-700 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
              <Target className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-earth-700">O4S.ai</span>
          </Link>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 md:px-8 py-10 md:py-16">

        {/* ─── DISCLAIMER ─── */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle className="w-7 h-7 text-amber-600" />
            <h1 className="text-3xl sm:text-4xl font-bold text-earth-700">Disclaimer</h1>
          </div>
          <p className="text-earth-500 text-sm mb-6">Last updated: March 2026</p>

          <div className="space-y-4 text-earth-600 text-sm leading-relaxed">
            <p>The information provided on O4S.ai is for general informational and educational purposes only. It is not a substitute for professional career counselling, financial advice, or educational guidance.</p>
            <p><strong>AI-Generated Content:</strong> Our career recommendations, income projections, and skill assessments are generated using artificial intelligence. While we strive for accuracy, AI-generated content may contain errors, outdated information, or suggestions that may not be suitable for your specific circumstances.</p>
            <p><strong>No Professional Advice:</strong> O4S.ai does not provide professional career counselling, financial planning, or legal advice. All career decisions should be made after consulting with qualified professionals.</p>
            <p><strong>Income Projections:</strong> Salary ranges and income projections shown on our platform are estimates based on general market data. Actual earnings vary significantly based on individual skills, experience, location, market conditions, and employer.</p>
            <p><strong>Third-Party Information:</strong> Course recommendations, college information, and external resources referenced on our platform are provided for convenience. We do not endorse, guarantee, or assume responsibility for the accuracy of third-party content.</p>
            <p><strong>User Responsibility:</strong> Any actions you take based on the information provided by O4S.ai are at your own risk. We are not responsible for any decisions made or outcomes resulting from the use of our platform.</p>
          </div>
        </section>

        {/* ─── SOCIAL SHARING GUIDELINES ─── */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <Share2 className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold text-earth-700">Social Sharing Guidelines</h2>
          </div>

          <p className="text-earth-600 text-sm leading-relaxed mb-4">We encourage users to share their career insights and recommendations with friends and peers. When sharing content from O4S.ai on platforms like WhatsApp, Facebook, LinkedIn, or others, please follow these guidelines:</p>

          <div className="space-y-3">
            {[
              { title: 'Share Respectfully', desc: 'Share your own results and insights. Do not share other people\'s career assessments or personal information without their consent.' },
              { title: 'No Spam', desc: 'Do not send bulk or repetitive messages containing O4S.ai links. Share organically with people who would genuinely benefit from career guidance.' },
              { title: 'Accurate Representation', desc: 'Do not modify or misrepresent the content of shared cards or results. The share images are generated to accurately reflect your analysis.' },
              { title: 'No False Claims', desc: 'Do not use O4S.ai content to make false claims about guaranteed income, jobs, or career outcomes. Our recommendations are guidance, not guarantees.' },
              { title: 'Respect Platform Rules', desc: 'Follow the community guidelines and terms of service of each social media platform when sharing O4S.ai content.' },
              { title: 'Value-Driven Sharing', desc: 'Share because it might help someone, not for personal promotion. A good share adds value to the recipient.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl border border-earth-200 p-4">
                <h3 className="font-semibold text-earth-700 text-sm mb-1">{item.title}</h3>
                <p className="text-earth-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ─── COPYRIGHT & OWNERSHIP ─── */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <Copyright className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold text-earth-700">Copyright & Ownership</h2>
          </div>

          <div className="space-y-3 text-earth-600 text-sm leading-relaxed">
            <p>All content, features, and functionality on O4S.ai are owned by O4S.ai and are protected by Indian and international copyright, trademark, and intellectual property laws.</p>
            <p><strong>What you own:</strong> Your personal data, assessment inputs, and the specific results generated for your profile belong to you. You may download, save, and share your personal results.</p>
            <p><strong>What we own:</strong> The platform design, codebase, FAQ database, career roadmaps, AI prompts, branding, and all other original content are the property of O4S.ai.</p>
            <p><strong>Prohibited uses:</strong> You may not copy, reproduce, modify, distribute, or commercially exploit any content from O4S.ai without prior written permission. This includes scraping data, copying FAQ content, or reproducing career roadmaps.</p>
            <p>&copy; 2026 O4S.ai. All rights reserved.</p>
          </div>
        </section>

        {/* ─── COOKIE USAGE ─── */}
        <section className="mb-14">
          <div className="flex items-center gap-3 mb-4">
            <Cookie className="w-6 h-6 text-primary" />
            <h2 className="text-2xl font-bold text-earth-700">Cookie & Storage Usage</h2>
          </div>

          <div className="space-y-3 text-earth-600 text-sm leading-relaxed">
            <p>O4S.ai uses browser local storage and cookies to provide a smooth user experience. Here is what we store:</p>
            <div className="bg-white rounded-xl border border-earth-200 p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-earth-200">
                    <th className="text-left py-2 pr-4 text-earth-700 font-semibold">Storage Item</th>
                    <th className="text-left py-2 text-earth-700 font-semibold">Purpose</th>
                  </tr>
                </thead>
                <tbody className="text-earth-600">
                  <tr className="border-b border-earth-100">
                    <td className="py-2 pr-4">Authentication token</td>
                    <td className="py-2">Keeps you logged in across page visits</td>
                  </tr>
                  <tr className="border-b border-earth-100">
                    <td className="py-2 pr-4">Lead form status</td>
                    <td className="py-2">Remembers if you have completed a lead form</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Analytics cookies</td>
                    <td className="py-2">Used by analytics tools (if enabled by admin) to understand usage</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>You can clear your browser storage at any time through your browser settings. This will log you out and reset your preferences.</p>
          </div>
        </section>

        {/* ─── DATA RETENTION ─── */}
        <section className="mb-10">
          <h2 className="text-2xl font-bold text-earth-700 mb-4">Data Retention Policy</h2>
          <div className="space-y-3 text-earth-600 text-sm leading-relaxed">
            <p>We retain your data only for as long as necessary to provide our services and fulfil the purposes described in our <Link to="/privacy" className="text-primary underline">Privacy Policy</Link>.</p>
            <div className="bg-white rounded-xl border border-earth-200 p-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-earth-200">
                    <th className="text-left py-2 pr-4 text-earth-700 font-semibold">Data Type</th>
                    <th className="text-left py-2 text-earth-700 font-semibold">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="text-earth-600">
                  <tr className="border-b border-earth-100">
                    <td className="py-2 pr-4">Account data</td>
                    <td className="py-2">Until you delete your account</td>
                  </tr>
                  <tr className="border-b border-earth-100">
                    <td className="py-2 pr-4">Assessment results</td>
                    <td className="py-2">Until you delete your account</td>
                  </tr>
                  <tr className="border-b border-earth-100">
                    <td className="py-2 pr-4">Skill2Income analyses</td>
                    <td className="py-2">12 months from creation</td>
                  </tr>
                  <tr className="border-b border-earth-100">
                    <td className="py-2 pr-4">Lead form submissions</td>
                    <td className="py-2">24 months from submission</td>
                  </tr>
                  <tr>
                    <td className="py-2 pr-4">Usage analytics</td>
                    <td className="py-2">Aggregated and anonymised</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p>You may request deletion of your data at any time. Requests will be processed within 30 days.</p>
          </div>
        </section>

        <div className="bg-surface-alt rounded-xl border border-earth-200 p-6 text-center">
          <p className="text-earth-600 italic text-sm leading-relaxed">"I used O4S.ai for my career growth, and the AI-guided recommendations were extremely helpful, a must-try tool for taking your career to the next level."</p>
        </div>
      </main>

      <footer className="px-4 md:px-8 py-8 bg-earth-700 text-white mt-auto">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm mb-4">
            <Link to="/about" className="text-earth-400 hover:text-white transition-colors">About</Link>
            <Link to="/founder" className="text-earth-400 hover:text-white transition-colors">Founder</Link>
            <Link to="/contact" className="text-earth-400 hover:text-white transition-colors">Contact</Link>
            <Link to="/privacy" className="text-earth-400 hover:text-white transition-colors">Privacy</Link>
            <Link to="/terms" className="text-earth-400 hover:text-white transition-colors">Terms</Link>
            <Link to="/legal" className="text-earth-400 hover:text-white transition-colors">Legal</Link>
          </div>
          <p className="text-earth-500 text-xs text-center">&copy; 2026 O4S.ai. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LegalPage;
